import {
  BriefGenerationWidgetKey,
  BriefGenerationWidgetSchema,
  CONTENT_TEST_ARTIFACT_KEY_PREFIX,
  EmailFlowWidgetSchema,
  SOCIAL_OPPORTUNITY_ARTIFACT_KEY_PREFIX,
  SAVED_STRATEGIC_TERRITORIES_ARTIFACT_KEY_PREFIX,
  SAVED_FILE_ARTIFACT_KEY_PREFIX,
  CopyGenerationWidgetKey,
  SAVED_COPY_ARTIFACT_KEY_PREFIX,
  ImageGenerationWidgetSchemaKey,
  SAVED_IMAGE_ARTIFACT_KEY_PREFIX,
  VideoGenerationWidgetSchemaKey,
  SAVED_VIDEO_ARTIFACT_KEY_PREFIX,
  SAVED_IDEATION_CAMPAIGN_ARTIFACT_KEY_PREFIX,
  SAVED_IDEATION_IDEA_ARTIFACT_KEY_PREFIX,
} from '@inference.engine/widgets/web';
import type { ProjectArtifactDetailDto } from '@inference.engine/workspace-api-client/web';
import { Spinner } from '@monksflow/monks-ui';
import { EmailFlowWidget } from '@/features/email-flow-widget';
import { logger } from '@/lib/utils/logger';
import { AssetPreview } from '@/pages/ProjectDetail/sections/AssetDetail/components/AssetPreview/AssetPreview';
import { PdfViewerProvider } from '@/pages/ProjectDetail/sections/AssetDetail/components/PdfViewer/pdfViewer.store';
import { useArtifactPreviewStore } from '../../store/artifactPreview.store';
import { ArtifactPreviewError } from './ArtifactPreviewError';
import { ArtifactPreviewFallback } from './ArtifactPreviewFallback';
import { ArtifactPreviewWidget } from './ArtifactPreviewWidget';
import { toAssetFile } from './assetFile.utils';
import { AudienceSessionArtifactPreview } from './AudienceSessionArtifactPreview';
import { BriefArtifactPreview } from './BriefArtifactPreview';
import { FocusGroupArtifactPreview } from './FocusGroupArtifactPreview';
import { ContentTestArtifactPreview } from './ContentTestArtifactPreview';
import { IqAnalysisArtifactPreview } from './IqAnalysisArtifactPreview';
import { OpportunityArtifactPreview } from './OpportunityArtifactPreview';
import { SavedFileArtifactPreview } from './SavedFileArtifactPreview';
import { SavedWidgetArtifactPreview } from './SavedWidgetArtifactPreview';
import { StrategicTerritoriesArtifactPreview } from './StrategicTerritoriesArtifactPreview';
import { IdeationArtifactPreview } from './IdeationArtifactPreview';
import { useProjectArtifactDetail } from './hooks/useProjectArtifactDetail';

interface Props {
  projectId: string;
  artifactId: string;
}

/**
 * Kind-routing renderer for the Library preview modal.
 *
 * Mirrors the chat side panel: widget kinds resolve via `ArtifactPreviewWidget`
 * (the same `getWidgetComponentSync` lookup `SidePanel` uses), so the registry-
 * bound wrappers (already pre-wired with `useArtifactExport` / `sendOrCreateChat`
 * deps via `lib/config/agents.config.ts`) render unchanged inside the Dialog.
 *
 * Non-widget kinds (`image` / `video` / `file`) route to `AssetPreview`. The
 * one twist: attachment-origin `image` / `video` rows are widget payloads
 * (ImageGenerationWidget / VideoWidget data carries multiple `assetId` entries,
 * not a flat downloadUrl), so they go through `ArtifactPreviewWidget` like
 * the other widget kinds. Upload-origin file/image/video rows are flat
 * project_file rows with mimeType + downloadUrl, so they keep the AssetPreview
 * path.
 *
 * `email` falls through to a direct `EmailFlowWidget` import because that
 * widget is not registered in `WIDGET_REGISTRY` and accepts no exportDeps.
 *
 * `misc` / `custom` / unknown kinds render the JSON fallback so the modal
 * never throws on an unexpected payload.
 */
export function ArtifactPreviewBody(props: Props) {
  const query = useProjectArtifactDetail(props.projectId, props.artifactId);

  if (query.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Spinner />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <ArtifactPreviewError
        error={query.error}
        onRetry={() => {
          void query.refetch();
        }}
        onClose={() => useArtifactPreviewStore.getState().close()}
      />
    );
  }

  return (
    <ArtifactPreviewContent
      detail={query.data}
      projectId={props.projectId}
      artifactId={props.artifactId}
    />
  );
}

interface ContentProps {
  detail: ProjectArtifactDetailDto;
  projectId: string;
  artifactId: string;
}

function ArtifactPreviewContent(props: ContentProps) {
  const { detail } = props;
  const { artifactKind, artifactKey, data, origin } = detail;

  // Saved image/video outputs (context-less, keyed `saved-image:`/`saved-video:`)
  // are NOT attachment-origin chat widgets — they must reach the switch below,
  // which fetches their snapshot by id and mounts the live widget. Exclude them
  // here so this early attachment-widget path doesn't swallow them (their
  // `saved-*` key isn't a registry widget key, and their detail `data` is empty).
  const isSavedWidgetArtifact =
    artifactKey?.startsWith(SAVED_IMAGE_ARTIFACT_KEY_PREFIX) ||
    artifactKey?.startsWith(SAVED_VIDEO_ARTIFACT_KEY_PREFIX);

  const isAttachmentMediaWidget =
    !isSavedWidgetArtifact &&
    origin === 'attachment' &&
    (artifactKind === 'image' || artifactKind === 'video');

  if (isAttachmentMediaWidget) {
    return (
      <ArtifactPreviewWidget
        artifactKey={artifactKey ?? null}
        data={data}
        fallback={<ArtifactPreviewFallback />}
      />
    );
  }

  switch (artifactKind) {
    case 'image':
      // Saved image-generation outputs are context-less widget snapshots —
      // mount them through the live image widget rather than the asset viewer.
      if (artifactKey?.startsWith(SAVED_IMAGE_ARTIFACT_KEY_PREFIX)) {
        return (
          <SavedWidgetArtifactPreview
            artifactId={props.artifactId}
            widgetKey={ImageGenerationWidgetSchemaKey}
          />
        );
      }
      return (
        <PdfViewerProvider>
          <AssetPreview file={toAssetFile(detail)} embedded />
        </PdfViewerProvider>
      );

    case 'video':
      // Saved video-generation outputs are context-less widget snapshots —
      // mount them through the live video widget rather than the asset viewer.
      if (artifactKey?.startsWith(SAVED_VIDEO_ARTIFACT_KEY_PREFIX)) {
        return (
          <SavedWidgetArtifactPreview
            artifactId={props.artifactId}
            widgetKey={VideoGenerationWidgetSchemaKey}
          />
        );
      }
      return (
        <PdfViewerProvider>
          <AssetPreview file={toAssetFile(detail)} embedded />
        </PdfViewerProvider>
      );

    case 'file':
      // Saved (context-less) file outputs — e.g. the translation agent's xlsx —
      // carry their asset id only in the inference-api data payload, so resolve
      // the download URL by id rather than relying on workspace-api enrichment.
      if (artifactKey?.startsWith(SAVED_FILE_ARTIFACT_KEY_PREFIX)) {
        return <SavedFileArtifactPreview detail={detail} artifactId={props.artifactId} />;
      }
      // AssetPreview mounts PdfViewer for PDFs, which reads the PdfViewer store.
      // AssetDetail / AssetComparePane provide it; the Library modal must too,
      // or a PDF preview throws. Harmless for image/video (no store reads).
      // `embedded`: the Library modal doesn't host the AssetDetail annotation
      // store or AssetDetailFullScreen, so the PDF viewer drops the annotation
      // overlay + Expand control that would be dead here.
      return (
        <PdfViewerProvider>
          <AssetPreview file={toAssetFile(detail)} embedded />
        </PdfViewerProvider>
      );

    case 'email': {
      // EmailFlowWidget is not in WIDGET_REGISTRY and takes no exportDeps —
      // import + render directly. `data` is `Record<string, unknown>`; validate
      // against the widget's Zod schema before mounting so a malformed payload
      // routes to the JSON fallback instead of crashing the modal.
      const parsed = EmailFlowWidgetSchema.safeParse(data);
      if (!parsed.success) {
        logger.error(
          'ArtifactPreviewBody: EmailFlowWidget payload failed schema validation',
          parsed.error,
        );
        return <ArtifactPreviewFallback />;
      }
      return <EmailFlowWidget data={parsed.data} readOnly />;
    }

    case 'brief': {
      // The `brief` kind covers two distinct families. The generated brief
      // (`brief-generation-widget:v1`) is deliberately NOT a mountable widget
      // — it's a generation flow with a thumbnail only — so it has no registry
      // entry to mount. Render its persisted snapshot directly via
      // `BriefArtifactPreview`, validating `data` against the widget schema
      // first (mirrors the `email` branch: validate, log, fall back). Any other
      // brief key (e.g. social-brief) IS a registered widget and routes through
      // the normal `ArtifactPreviewWidget` registry lookup.
      if (artifactKey === BriefGenerationWidgetKey) {
        const parsed = BriefGenerationWidgetSchema.safeParse(data);
        if (!parsed.success) {
          logger.error(
            'ArtifactPreviewBody: BriefGenerationWidget payload failed schema validation',
            parsed.error,
          );
          return <ArtifactPreviewFallback />;
        }
        return (
          <BriefArtifactPreview
            brief={parsed.data.brief}
            projectId={props.projectId}
            artifactId={props.artifactId}
          />
        );
      }
      return (
        <ArtifactPreviewWidget
          artifactKey={artifactKey ?? null}
          data={data}
          fallback={<ArtifactPreviewFallback />}
        />
      );
    }

    case 'social_opportunities': {
      // Saved-opportunity artifacts carry a per-opportunity key and store a
      // self-contained opportunity snapshot, rendered as the rich detail card
      // (resolved by artifact id). Any other social_opportunities artifact (e.g.
      // the chat grid widget) keeps the registry path.
      if (artifactKey?.startsWith(SOCIAL_OPPORTUNITY_ARTIFACT_KEY_PREFIX)) {
        return <OpportunityArtifactPreview artifactId={props.artifactId} />;
      }
      return (
        <ArtifactPreviewWidget
          artifactKey={artifactKey ?? null}
          data={data}
          fallback={<ArtifactPreviewFallback />}
        />
      );
    }

    case 'strategy': {
      if (artifactKey?.startsWith(SAVED_STRATEGIC_TERRITORIES_ARTIFACT_KEY_PREFIX)) {
        return <StrategicTerritoriesArtifactPreview artifactId={props.artifactId} />;
      }
      return (
        <ArtifactPreviewWidget
          artifactKey={artifactKey ?? null}
          data={data}
          fallback={<ArtifactPreviewFallback />}
        />
      );
    }

    case 'focus_group': {
      if (!artifactKey) return <ArtifactPreviewFallback />;
      return <FocusGroupArtifactPreview artifactKey={artifactKey} />;
    }

    case 'iq_analysis': {
      if (!artifactKey) return <ArtifactPreviewFallback />;
      return <IqAnalysisArtifactPreview artifactKey={artifactKey} />;
    }

    case 'audience_session':
      return <AudienceSessionArtifactPreview artifactId={props.artifactId} />;

    case 'content_test': {
      if (artifactKey?.startsWith(CONTENT_TEST_ARTIFACT_KEY_PREFIX)) {
        return <ContentTestArtifactPreview artifactKey={artifactKey} />;
      }
      return <ArtifactPreviewFallback />;
    }

    case 'copy':
      // Saved copy-generation outputs are context-less widget snapshots —
      // mount them through the live copy widget by id. Any other copy artifact
      // keeps the registry path below.
      if (artifactKey?.startsWith(SAVED_COPY_ARTIFACT_KEY_PREFIX)) {
        return (
          <SavedWidgetArtifactPreview
            artifactId={props.artifactId}
            widgetKey={CopyGenerationWidgetKey}
          />
        );
      }
      return (
        <ArtifactPreviewWidget
          artifactKey={artifactKey ?? null}
          data={data}
          fallback={<ArtifactPreviewFallback />}
        />
      );

    case 'ideation': {
      if (artifactKey?.startsWith(SAVED_IDEATION_CAMPAIGN_ARTIFACT_KEY_PREFIX)) {
        return <IdeationArtifactPreview artifactId={props.artifactId} variant="campaign" />;
      }
      if (artifactKey?.startsWith(SAVED_IDEATION_IDEA_ARTIFACT_KEY_PREFIX)) {
        return <IdeationArtifactPreview artifactId={props.artifactId} variant="idea" />;
      }
      return (
        <ArtifactPreviewWidget
          artifactKey={artifactKey ?? null}
          data={data}
          fallback={<ArtifactPreviewFallback />}
        />
      );
    }

    case 'audience':
    case 'insight':
      return (
        <ArtifactPreviewWidget
          artifactKey={artifactKey ?? null}
          data={data}
          fallback={<ArtifactPreviewFallback />}
        />
      );

    case 'misc':
    case 'custom':
    default:
      return <ArtifactPreviewFallback />;
  }
}
